import { ICdicCoupon, IClient, ICoupon, ICouponProgram, ICouponService, IDropdownLabel } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, DatePicker, Select } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { PATH_CDIC_COUPON_LIST, ageGroups, cdicServiceCategory, dateFormat } from "@configs";
import { useDispatch, useSelector } from "react-redux";
import { selectApp, setLoading } from "@redux";
import { clientApi, couponApi } from "@api";
import { useNotify } from "@utils";
import { useHistory } from "react-router";
import dayjs, { Dayjs } from "dayjs";
import { SharedButton, SharedIconButton } from "@components";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { ComponentCdicClient } from "./CdicClientCard";
const { RangePicker } = DatePicker;
const style = { background: "#00b96b" };
interface IProps {
  coupon?: ICoupon;
}
const dateStyle = { width: "100%", height: 48 };
const rangePresets: {
  label: string;
  value: [Dayjs, Dayjs];
}[] = [
  { label: "Last 7 Days", value: [dayjs().add(-7, "d"), dayjs()] },
  { label: "Last 14 Days", value: [dayjs().add(-14, "d"), dayjs()] },
  { label: "Last 30 Days", value: [dayjs().add(-30, "d"), dayjs()] },
  { label: "Last 60 Days", value: [dayjs().add(-60, "d"), dayjs()] },
];

const initValue: Partial<ICdicCoupon> = {
  validFrom: "",
  validTo: "",
  userId: "",
  programId: "",
  programName: "CDiC Program",
  serviceCategory: "",
};

export const GenerateCdicCouponForm = memo(({ coupon }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupGenerateCdicCoupon } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const generateCouponSchema = Yup.object().shape(YupGenerateCdicCoupon);
  const [genereateForm] = Form.useForm();
  const [client, setClient] = useState<IClient | null>(null);
  const { loading } = useSelector(selectApp);
  const [fetching, setFetching] = useState(false);
  const [family, setFamily] = useState<IDropdownLabel[]>([]);
  const [service, setService] = useState<IDropdownLabel[]>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);

  
  const onFinish = async (values: ICoupon) => {
    const { date } = values;
    const from = date ? date[0] : "";
    const to = date ? date[1] : "";
    try {
      dispatch(setLoading(true));
      await couponApi.generateCdic({
        programName: "CDiC Program",
        serviceCategory: values.serviceCategory || "",
        programId: values.programId || "",
        validFrom: dayjs(from).format(),
        validTo: dayjs(to).format(),
        userId: client ? client?.userId : "",
        status: "ACTIVE",
      });
      success(t("message.create.success"));
      history.push(PATH_CDIC_COUPON_LIST);
    } catch (err: any) {
      error(t("message.create.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await generateCouponSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const phoneNum: string = Form.useWatch("userId", genereateForm);
  
  const handleSearch = async () => {
    try {
      dispatch(setLoading(true));
      const res = await clientApi.clientPhone(phoneNum);
      
      if (res.data) {
        setClient(res.data);
        success(t("page.found_a_patient"));
      } else {
        setClient(null);
        error(t("message.not_found_patient"));
      }
    } catch (err: any) {
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    searchFamily();
    searchService();
  }, ['searchProgram']);

  const getProgramValues = (data: ICouponProgram[]) => {
    return data.reduce<IDropdownLabel[]>((a, c) => {
      a.push({ ...c, value: c?._id || "", label: c?.programName || "" });
      return a;
    }, []);
  };

  const getServiceValues = (data: ICouponService[]) => {
    return data.reduce<IDropdownLabel[]>((a, c) => {
      a.push({ ...c, value: c?.serviceName || "", label: c?.serviceName || "" });
      return a;
    }, []);
  };
  const searchService = async () => {
    try {
      setFetching(true);
      const res = await couponApi.searchServiceNames();
      setService(getServiceValues(res.data));
      setFetching(false);
    } catch (err: any) {
      // error(err.message);
      setFetching(false);
      throw new Error(err.message);
    } finally {
      setFetching(false);
    }
  };
  const searchFamily = async () => {
    try {
      setFetching(true);
      const res = await couponApi.searchProgramName();
      
      setFamily(getProgramValues(res.data));
      setFetching(false);
    } catch (err: any) {
      // error(err.message);
      setFetching(false);
      throw new Error(err.message);
    } finally {
      setFetching(false);
    }
  };

  const goBack = () => history.goBack();
  return (
    <Form
      form={genereateForm}
      name="basic"
      ref={formRef}
      initialValues={initValue}
      onFinish={onFinish}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.program_name")} name="programId" rules={[yupSync]}>
            <Select defaultValue={""}>            
              {family.map((item, index) => (
                <Select.Option value={item.value} key={index}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24}>
          <Form.Item label={t("page.service_category")} name="serviceCategory" rules={[yupSync]}>
            <Select>            
              {service.map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.valid_from_to")} name="date" rules={[yupSync]}>
            <RangePicker
              format={dateFormat}
              style={dateStyle}
              presets={rangePresets}
              size="large"
              onCalendarChange={(dates) => {
                if (dates && dates[0]) {
                  setStartDate(dates[0]);
                } else {
                  setStartDate(null);
                }
              }}
              disabledDate={(current: Dayjs) => {
                if (!startDate) return false;
                const twoMonthsLater = startDate.add(2, 'month');
                return current && current.isAfter(twoMonthsLater, 'day');
              }}
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item name={"userId"} label={t("page.dedicated_patient_number")} rules={[yupSync]}>
            <Row gutter={20}>
              <Col span={20}>
                <Input placeholder={t("page.placeholder_phone")} />
              </Col>
              <Col span={4}>
                <SharedIconButton onClick={handleSearch} icon={<SearchOutlined />} style={style} />
              </Col>
            </Row>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <StyledFormAction>
          <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
            {t("page.cancel")}
          </SharedButton>
          <SharedButton type="primary" htmlType="submit" size="middle" disabled={!client}>
            {t("page.create")}
          </SharedButton>
        </StyledFormAction>
      </Form.Item>
      {client && <ComponentCdicClient loading={loading} client={client} />}
    </Form>
  );
});
const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
